'use client';
import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useCertificates } from '@/contexts/CertificatesContext';
import { groupExpiryTimeline } from '@/lib/certificate-alerts';
import type { Certificate } from '@/lib/db';

const PREVIEW_COUNT = 3;

function formatDaysContext(cert: Certificate): string {
  if (cert.status === 'no-expiry') return 'No expiry';
  if (cert.daysRemaining === null) return 'Date unknown';
  if (cert.daysRemaining < 0) {
    const n = Math.abs(cert.daysRemaining);
    return `Expired ${n}d ago`;
  }
  if (cert.daysRemaining === 0) return 'Expires today';
  return `${cert.daysRemaining}d remaining`;
}

function formatDateContext(cert: Certificate, groupKey: string): string {
  if (groupKey === 'verifyRequirement') return 'Verify requirement';
  if (groupKey === 'dateUnknown') return 'Date unknown';

  const days = formatDaysContext(cert);
  if (!cert.expiryDate || cert.expiryDate === 'N/A') return days;
  return `${cert.expiryDate} · ${days}`;
}

type GroupEntry = {
  key: string;
  label: string;
  certs: Certificate[];
  fgColor: string;
  neutral: boolean;
};

export default function ExpiryTimeline() {
  const { certificates } = useCertificates();
  const groups = useMemo(() => groupExpiryTimeline(certificates), [certificates]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const groupEntries: GroupEntry[] = [
    { key: 'expired',           label: 'Expired',           certs: groups.expired,           fgColor: 'var(--status-critical)',      neutral: false },
    { key: 'within30',          label: 'Within 30 days',    certs: groups.within30,          fgColor: 'var(--status-critical)',      neutral: false },
    { key: 'within90',          label: 'Within 90 days',    certs: groups.within90,          fgColor: 'var(--status-expiring-soon)', neutral: false },
    { key: 'dateUnknown',       label: 'Date unknown',      certs: groups.dateUnknown,       fgColor: '',                           neutral: true  },
    { key: 'verifyRequirement', label: 'Verify requirement',certs: groups.verifyRequirement, fgColor: '',                           neutral: true  },
    { key: 'longTermValid',     label: 'Long-term valid',   certs: groups.longTermValid,     fgColor: 'var(--status-valid)',         neutral: false },
  ].filter((g) => g.certs.length > 0);

  if (groupEntries.length === 0) return null;

  return (
    <div className="card-elevated overflow-hidden">
      <div className="px-4 pt-3 pb-2 border-b border-border">
        <h2 className="text-xs font-600 text-muted-foreground uppercase tracking-wide">Expiry Timeline</h2>
      </div>
      <div className="divide-y divide-border">
        {groupEntries.map((group) => {
          const isExpanded = expanded[group.key] ?? false;
          const showToggle = group.certs.length > PREVIEW_COUNT;
          const visible = showToggle && !isExpanded ? group.certs.slice(0, PREVIEW_COUNT) : group.certs;
          const hidden = group.certs.length - PREVIEW_COUNT;

          return (
            <div key={group.key} className="px-4 py-2.5">
              <div className="flex items-center justify-between mb-1.5">
                <span
                  className={`text-xs font-600 uppercase tracking-wide${group.neutral ? ' text-muted-foreground' : ''}`}
                  style={group.neutral ? undefined : { color: group.fgColor }}
                >
                  {group.label}
                </span>
                <span className="text-xs font-500 text-muted-foreground tabular-nums">{group.certs.length}</span>
              </div>

              <div id={`${group.key}-list`} className="space-y-1.5">
                {visible.map((cert) => (
                  <div key={cert.id} className="flex items-start gap-2 min-w-0">
                    <span
                      className={`mt-[5px] shrink-0 w-1.5 h-1.5 rounded-full${group.neutral ? ' bg-muted-foreground' : ''}`}
                      style={group.neutral ? undefined : { backgroundColor: group.fgColor }}
                    />
                    <div className="min-w-0 flex-1 sm:flex sm:items-baseline sm:justify-between sm:gap-4">
                      <p className="text-sm text-foreground leading-snug break-words">{cert.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 sm:mt-0 sm:text-right sm:shrink-0 break-words">
                        {cert.certNumber && cert.certNumber !== 'N/A' && (
                          <span className="font-mono">{cert.certNumber} · </span>
                        )}
                        {formatDateContext(cert, group.key)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {showToggle && (
                <button
                  type="button"
                  aria-expanded={isExpanded}
                  aria-controls={`${group.key}-list`}
                  onClick={() => setExpanded((prev) => ({ ...prev, [group.key]: !isExpanded }))}
                  className="mt-2.5 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {isExpanded ? (
                    <><ChevronUp size={12} />Show less</>
                  ) : (
                    <><ChevronDown size={12} />Show {hidden} more</>
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
